'use client'

import { useTranslations } from 'next-intl'
import { Link } from '@/i18n/navigation'
import { useState, useEffect } from 'react'
import SocialSignIn from '../SocialSignIn'
import Logo from '@/app/components/Layout/Header/Logo'
import { createClient } from '@/lib/supabase/client'
import { Icon } from '@iconify/react/dist/iconify.js'
import PasswordInput from '@/app/components/SharedComponent/PasswordInput'

const Signin = ({
  onSuccess,
  onSwitchToSignUp,
  onSwitchToForgotPassword,
  redirectTo,
}: {
  onSuccess?: () => void
  onSwitchToSignUp?: () => void
  onSwitchToForgotPassword?: () => void
  redirectTo?: string
}) => {
  const t = useTranslations('SignIn')
  const signUpHref = redirectTo ? `/signup?redirect=${encodeURIComponent(redirectTo)}` : '/signup'
  const isTeacherFlow = redirectTo === '/account/become-teacher'
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [loader, setLoader] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [forgotLoading, setForgotLoading] = useState(false)
  const [signUpLoading, setSignUpLoading] = useState(false)

  // The auth callback route redirects here with ?error=auth_callback_failed
  // when Google's own code exchange fails (expired/reused code, cancelled
  // consent, etc.) — previously silent (nothing ever read this param), so a
  // failed Google sign-in just bounced back to this page with NO message,
  // easily confused for "Google sign-in isn't working" with no clue why.
  // Read via plain window.location (not useSearchParams) so this client
  // component doesn't force a Suspense boundary on its server page.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('error') === 'auth_callback_failed') {
      setError(t('googleSignInFailed'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoader(true)

    try {
      const supabase = createClient()
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      })

      if (error) {
        setLoader(false)
        // Supabase's own wording for a wrong email/password is identical
        // whether the account never had a password at all (every Google
        // sign-up) or someone just mistyped it — real, recurring support
        // reports trace back to the first case: they signed in with Google
        // once, came back after their session expired, and out of habit
        // typed into this form instead of tapping the Google button again.
        // No account lookup here (that would leak whether an email is
        // registered) — just a wording fix that covers both real causes.
        setError(error.message === 'Invalid login credentials' ? t('invalidCredentialsHint') : (error.message || t('genericError')))
        return
      }

      setLoader(false)
      setRedirecting(true)

      if (onSuccess) {
        onSuccess()
      } else {
        window.location.href = redirectTo || '/dashboard'
      }
    } catch {
      setLoader(false)
      setError(t('unexpectedError'))
    }
  }

  if (redirecting) {
    return (
      <div className='flex flex-col items-center justify-center py-12 gap-4'>
        <div className='w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center'>
          <Icon icon='mdi:loading' width={32} height={32} className='animate-spin text-primary' />
        </div>
        <p className='text-gray-700 font-medium'>{t('signingIn')}</p>
        <p className='text-sm text-gray-400'>{redirectTo ? t('redirectingBack') : t('redirectingToDashboard')}</p>
      </div>
    )
  }

  return (
    <>
      <div className='mb-10 text-center mx-auto inline-block'>
        <Logo />
      </div>

      {isTeacherFlow && (
        <p className='mb-6 rounded-lg bg-primary/10 px-4 py-3 text-sm text-primary'>
          {t('teacherFlowNotice')}
        </p>
      )}

      <SocialSignIn />

      <span className='z-1 relative my-8 block text-center  before:absolute before:h-px before:w-[40%] before:bg-black/20 before:left-0 before:top-3  after:absolute after:h-px after:w-[40%] after:bg-black/20 after:top-3 after:right-0'>
        <span className='text-body-secondary relative z-10 inline-block px-3 text-base text-black'>
          {t('or')}
        </span>
      </span>

      {error && <p className='mb-4 text-red-600 text-sm'>{error}</p>}

      <form onSubmit={handleSubmit}>
        <div className='mb-[22px]'>
          <input
            type='email'
            name='email'
            placeholder={t('emailPlaceholder')}
            required
            value={formData.email}
            onChange={handleChange}
            className='w-full rounded-xl border border-solid bg-transparent px-5 py-3 text-base text-dark outline-hidden transition border-gray-200 placeholder:text-black/30 focus:border-primary focus:ring-2 focus:ring-primary/15 focus-visible:shadow-none text-black'
          />
        </div>
        <div className='mb-1'>
          <PasswordInput
            name='password'
            placeholder={t('passwordPlaceholder')}
            required
            value={formData.password}
            onChange={handleChange}
            className='w-full rounded-xl border border-solid bg-transparent px-5 py-3 text-base text-dark outline-hidden transition border-gray-200 placeholder:text-black/30 focus:border-primary focus:ring-2 focus:ring-primary/15 focus-visible:shadow-none text-black'
          />
        </div>
        <div className='mb-[22px] text-right'>
          {onSwitchToForgotPassword ? (
            <button
              type='button'
              disabled={forgotLoading}
              onClick={() => { setForgotLoading(true); onSwitchToForgotPassword() }}
              className='inline-flex items-center gap-1.5 text-sm text-primary hover:underline disabled:opacity-60'>
              {forgotLoading && <Icon icon='mdi:loading' width={14} className='animate-spin' />}
              {forgotLoading ? t('loading') : t('forgotPassword')}
            </button>
          ) : (
            <Link
              href='/forgot-password'
              onClick={() => setForgotLoading(true)}
              aria-busy={forgotLoading}
              className='inline-flex items-center gap-1.5 text-sm text-primary hover:underline'>
              {forgotLoading && <Icon icon='mdi:loading' width={14} className='animate-spin' />}
              {forgotLoading ? t('loading') : t('forgotPassword')}
            </Link>
          )}
        </div>
        <div className='mb-9'>
          <button
            type='submit'
            disabled={loader}
            aria-busy={loader}
            className='inline-flex items-center justify-center gap-2 bg-primary w-full py-3 rounded-full text-18 font-medium border text-white border-primary hover:text-primary hover:bg-transparent hover:cursor-pointer transition duration-300 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed'>
            {loader && <Icon icon='mdi:loading' width={20} className='animate-spin' />}
            {loader ? t('signingInButton') : t('signIn')}
          </button>
        </div>
      </form>

      <p className='text-black text-base'>
        {t('notAMember')}{' '}
        {onSwitchToSignUp ? (
          <button
            type='button'
            disabled={signUpLoading}
            onClick={() => { setSignUpLoading(true); onSwitchToSignUp() }}
            className='inline-flex items-center gap-1.5 text-primary hover:underline hover:cursor-pointer disabled:opacity-60'>
            {signUpLoading && <Icon icon='mdi:loading' width={14} className='animate-spin' />}
            {signUpLoading ? t('loading') : t('signUp')}
          </button>
        ) : (
          <Link
            href={signUpHref}
            onClick={() => setSignUpLoading(true)}
            aria-busy={signUpLoading}
            className='inline-flex items-center gap-1.5 text-primary hover:underline'>
            {signUpLoading && <Icon icon='mdi:loading' width={14} className='animate-spin' />}
            {signUpLoading ? t('loading') : t('signUp')}
          </Link>
        )}
      </p>
    </>
  )
}

export default Signin
